import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CreditCard, Plus, Trash2, CheckCircle2, AlertTriangle, Loader2, ArrowLeft } from 'lucide-react';
import { loadStripe, type Stripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import {
  fetchPaymentMethods, createPaymentSetupIntent,
  setDefaultPaymentMethod, deletePaymentMethod, fetchOutstanding,
  payOutstanding,
  formatCents,
  type PaymentMethodsResponse,
  type OutstandingResponse,
} from '@/lib/api';
import { PageHeader } from '@/components/PageHeader';
import { toast } from '@/store/toast';
import { useAuthStore } from '@/store/auth';
import formStyles from '@/components/Form.module.css';
import styles from './PaymentMethodsPage.module.css';
import { FormError, useFormError } from '@/components/ui/FormError';
import { readApiErrorEnvelope } from '@/lib/apiErrorEnvelope';
import { balanceReturnPath } from '@/lib/balanceReturn';
import { confirmedSetupCard, needsExpiredDefaultPrompt, setupCardReturn } from '@/lib/addedCardDefault';

interface Props { onNavigate: (path: string) => void }

const STRIPE_KEY = import.meta.env['VITE_STRIPE_KEY'] as string | undefined;
let stripePromise: Promise<Stripe | null> | null = null;
function getStripeP(): Promise<Stripe | null> {
  if (!STRIPE_KEY) return Promise.resolve(null);
  if (!stripePromise) stripePromise = loadStripe(STRIPE_KEY);
  return stripePromise;
}

const BRAND_LABEL: Record<string, string> = {
  visa: 'Visa', mastercard: 'Mastercard', amex: 'Amex', discover: 'Discover',
  jcb: 'JCB', diners: 'Diners', unionpay: 'UnionPay',
};

export function PaymentMethodsPage({ onNavigate }: Props) {
  const { t } = useTranslation('billing');
  const qc = useQueryClient();
  const refreshUser = useAuthStore((s) => s.refreshUser);
  const [showAdd, setShowAdd] = useState(false);
  const [paying, setPaying] = useState<string | null>(null);
  const [selectedCard, setSelectedCard] = useState('');
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [addError, setAddError] = useFormError();
  const [paymentError, setPaymentError] = useFormError();
  const [resolved, setResolved] = useState(false);
  const [defaultPrompt, setDefaultPrompt] = useState<string | null>(null);
  const [savingDefault, setSavingDefault] = useState(false);
  const [defaultError, setDefaultError] = useFormError();
  const promptRef = useRef<HTMLElement | null>(null);
  const [setupReturn] = useState(() => setupCardReturn(new URL(window.location.href)));
  const returnHandled = useRef(false);

  const pmQ = useQuery({ queryKey: ['payment-methods'], queryFn: fetchPaymentMethods });
  const outstandingQ = useQuery({ queryKey: ['outstanding'], queryFn: fetchOutstanding,
    refetchInterval: q => q.state.data?.entries.some(e => e.status === 'processing') ? 5000 : false });
  const paymentCard = selectedCard || pmQ.data?.default_payment_method || pmQ.data?.payment_methods[0]?.id || '';
  const entries = outstandingQ.data?.entries ?? [];
  const hasPayable = entries.some(e => e.payable && (!e.action || e.action === 'pay'));
  const reviewOnly = entries.length > 0 && outstandingQ.data?.blocks_charging === false
    && entries.every(e => e.status === 'review' && e.action === 'refresh' && !e.payable);
  const continueCharging = () => onNavigate(balanceReturnPath(new URLSearchParams(window.location.search).get('return_to')));

  const startAdd = async () => {
    setAddError('');
    setDefaultPrompt(null);
    setDefaultError('');
    if (!STRIPE_KEY) {
      setAddError(t('errors.stripeKeyMissing'));
      setShowAdd(true);
      return;
    }
    try {
      const intent = await createPaymentSetupIntent();
      setClientSecret(intent.client_secret);
      setShowAdd(true);
    } catch (err) {
      setAddError(err instanceof Error ? err.message : t('errors.initFailed'));
      setShowAdd(true);
    }
  };

  // Preserve first-card defaulting. An existing expired default is replaced
  // only when the driver explicitly accepts the prompt, including bank return.
  const onAdded = useCallback(async (newPaymentMethodId: string | null) => {
    setShowAdd(false);
    setClientSecret(null);

    if (newPaymentMethodId) {
      setSelectedCard(newPaymentMethodId);
      const cached = qc.getQueryData<PaymentMethodsResponse>(['payment-methods']);
      const current = await qc.fetchQuery({ queryKey: ['payment-methods'], queryFn: fetchPaymentMethods, staleTime: 0 }).catch(() => cached);
      const hasExistingDefault = Boolean(current?.default_payment_method);
      if (needsExpiredDefaultPrompt(current, newPaymentMethodId)) {
        setDefaultError('');
        setDefaultPrompt(newPaymentMethodId);
      } else if (current && !hasExistingDefault) {
        try {
          await setDefaultPaymentMethod(newPaymentMethodId);
        } catch (err) {
          // Don't block. The server-side webhook handler is the
          // structural backstop; this client call is the latency
          // optimization, not the correctness guarantee.
          // eslint-disable-next-line no-console
          console.warn(
            '[payment] auto-set-default failed, webhook is the backstop:',
            err,
          );
        }
      }
    }

    qc.invalidateQueries({ queryKey: ['payment-methods'] });
    if (hasPayable) toast(t('outstanding.cardSaved', {
      defaultValue: 'Card saved. Tap Pay now to clear your balance.',
    }), { tone: 'accent' });
    // Driver-friction fix: keep the auth store's `has_payment_method`
    // flag in sync so MapPage's onboarding card unmounts and
    // PreSessionPage's start-charge modal stops intercepting.
    void refreshUser();
  }, [qc, refreshUser, hasPayable, t, setDefaultError]);

  useEffect(() => {
    if (!setupReturn.secret || returnHandled.current) return;
    window.history.replaceState(window.history.state, '', setupReturn.cleanUrl);
    let active = true;
    void (async () => {
      const stripe = await getStripeP();
      if (!stripe) throw new Error('Stripe unavailable');
      const result = await stripe.retrieveSetupIntent(setupReturn.secret!);
      if (!active || returnHandled.current) return;
      returnHandled.current = true;
      const card = confirmedSetupCard(result.setupIntent);
      if (!card) throw new Error('Card not confirmed');
      await onAdded(card);
    })().catch(() => {
      if (active) { setAddError(t('errors.saveFailed')); setShowAdd(true); }
    });
    return () => { active = false; };
  }, [setupReturn, onAdded, setAddError, t]);

  useEffect(() => {
    if (!defaultPrompt) return;
    promptRef.current?.focus({ preventScroll: true });
    promptRef.current?.scrollIntoView({ block: 'center', behavior: 'auto' });
  }, [defaultPrompt]);

  const handleSetDefault = async (id: string) => {
    try {
      await setDefaultPaymentMethod(id);
      setDefaultPrompt(null);
      qc.invalidateQueries({ queryKey: ['payment-methods'] });
      void qc.invalidateQueries({ queryKey: ['start-preview'] });
      // /auth/me re-derives has_payment_method from
      // (stripe_customer_id && default_stripe_payment_method_id);
      // setting a default flips the flag → refresh.
      void refreshUser();
      return true;
    } catch (err) {
      toast(err instanceof Error ? err.message : t('errors.setDefaultFailed'), { tone: 'error' });
      setDefaultError(t('errors.setDefaultFailed'));
      return false;
    }
  };

  const acceptDefault = async () => {
    if (!defaultPrompt || savingDefault) return;
    setSavingDefault(true);
    setDefaultError('');
    try {
      if (await handleSetDefault(defaultPrompt)) {
        setDefaultPrompt(null);
        toast(t('expiredDefault.updated'), { tone: 'accent' });
      }
    } finally { setSavingDefault(false); }
  };

  const handlePay = async (entry: OutstandingResponse['entries'][number]) => {
    const { session_id: sessionId } = entry;
    const kind = entry.kind === 'idle' ? 'idle' : undefined;
    setPaymentError('');
    setPaying(`${sessionId}:${entry.kind ?? 'principal'}`);
    try {
      if (entry.action === 'refresh' || (!entry.action && !entry.payable)) {
        await outstandingQ.refetch(); return;
      }
      const card = paymentCard || undefined;
      if (entry.action !== 'reconcile' && !card) { await startAdd(); return; }
      let result = await payOutstanding(sessionId, card, kind);
      if (result.client_secret) {
        const stripe = await getStripeP();
        if (!stripe) throw Error(t('errors.initFailed'));
        const confirmation = await stripe.confirmCardPayment(result.client_secret);
        if (confirmation.error) throw Error(confirmation.error.message ?? t('errors.saveFailed'));
        result = await payOutstanding(sessionId, card, kind);
      }
      if (result.status === 'succeeded') setResolved(true);
      else if (result.status !== 'processing') setPaymentError(t('outstanding.tryAnotherCard', {
        defaultValue: 'Payment did not complete. Try another card. Your balance is still owed.',
      }));
      toast(result.status === 'succeeded'
        ? t('outstanding.paid', { defaultValue: 'Payment received.' })
        : t('outstanding.stillOwed', { defaultValue: 'Payment has not completed. Your balance remains outstanding.' }),
      { tone: result.status === 'succeeded' ? 'accent' : 'neutral' });
    } catch (err) {
      const { message } = await readApiErrorEnvelope(err);
      setPaymentError(message ?? t('outstanding.paymentFailed', { defaultValue: 'Could not complete payment. Please try again or choose another card.' }));
    } finally {
      setPaying(null);
      void qc.invalidateQueries({ queryKey: ['outstanding'] });
      void qc.invalidateQueries({ queryKey: ['start-preview'] });
      void refreshUser();
    }
  };

  const handleDelete = async (id: string, last4?: string) => {
    const message = last4 ? t('confirm.removeCardEnding', { last4 }) : t('confirm.removeCard');
    if (!confirm(message)) return;
    try {
      await deletePaymentMethod(id);
      qc.invalidateQueries({ queryKey: ['payment-methods'] });
      // Removing the default card flips has_payment_method back to
      // false; refresh so the onboarding card reappears on next
      // map visit.
      void refreshUser();
    } catch (err) {
      toast(err instanceof Error ? err.message : t('errors.removeFailed'), { tone: 'error' });
    }
  };

  const elementsOptions = useMemo(() => clientSecret ? { clientSecret, appearance: { theme: 'night' as const, variables: { colorPrimary: '#2ECC71' } } } : null, [clientSecret]);

  return (
    <div className={styles.page}>
      <button className={styles.back} onClick={() => onNavigate('/profile')}>
        <ArrowLeft size={16} /> {t('back')}
      </button>

      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        iconSlot="payment-card-add"
        iconFallback={CreditCard}
      />

      {reviewOnly && <section className={styles.cleared} role="status">
        <CheckCircle2 size={20} />
        <strong>{t('outstanding.canCharge')}</strong>
        {entries.map(e => <span key={`${e.session_id}:${e.kind}`}>{t('outstanding.reviewRemaining', { amount: formatCents(e.amount_cents, e.currency) })}</span>)}
        <span>{t('outstanding.noAction')}</span>
        <button className={formStyles.btnPrimary} onClick={continueCharging}>{t('outstanding.continueCharging')}</button>
      </section>}
      {!reviewOnly && Boolean(outstandingQ.data?.entries.length) && (
        <section className={styles.balanceSection} aria-label={t('outstanding.label')}>
          {hasPayable && <>
          <label htmlFor="balance-card">{t('outstanding.chooseCard', { defaultValue: 'Card for this payment' })}</label>
          <select id="balance-card" className={styles.balanceSelect} disabled={paying !== null} value={paymentCard} onChange={e => setSelectedCard(e.target.value)}>
            <option value="">{t('outstanding.chooseCardPlaceholder', { defaultValue: 'Choose a saved card, or add a new card below' })}</option>
            {pmQ.data?.payment_methods.map(pm => <option key={pm.id} value={pm.id}>{pm.brand} •••• {pm.last4}</option>)}
          </select></>}
          {outstandingQ.data?.entries.map(entry => (
            <div key={`${entry.session_id}:${entry.kind ?? 'principal'}`} className={styles.outstandingCard}>
              <AlertTriangle size={20} />
              <div style={{ flex: 1 }}>
                <p className={styles.outstandingLabel}>{entry.kind === 'idle' ? t('outstanding.idle', { defaultValue: 'Idle fee after charging' }) : t('outstanding.label')}</p>
                <p className={styles.outstandingAmount}>{formatCents(entry.amount_cents, entry.currency)}</p>
                {entry.action === 'reconcile' && <p>{t('outstanding.reconcileHelp', { defaultValue: 'Payment was received. Update its status to resume charging.' })}</p>}
                {entry.action === 'refresh' && <p>{t('outstanding.checking', { defaultValue: 'We are checking the earlier payment. Refresh its status before paying again.' })}</p>}
              </div>
              <button className={styles.payNowBtn} disabled={paying !== null || pmQ.isLoading} onClick={() => void handlePay(entry)}>
                {paying === `${entry.session_id}:${entry.kind ?? 'principal'}` ? <Loader2 size={18} />
                  : entry.action === 'reconcile' ? t('outstanding.updateStatus', { defaultValue: 'Update payment status' })
                  : entry.action === 'refresh' || (!entry.action && !entry.payable) ? t('outstanding.refresh', { defaultValue: 'Refresh balance' })
                  : t('outstanding.payAmount', { defaultValue: 'Pay {{amount}} now', amount: formatCents(entry.amount_cents, entry.currency) })}
              </button>
            </div>
          ))}
          {paymentError && <FormError announce={paymentError.nonce}>{paymentError.message}</FormError>}
        </section>
      )}
      {outstandingQ.isError && <div className={styles.notice} role="alert">
        <span>{t('outstanding.loadFailed', { defaultValue: 'Your balance could not be loaded. Please try again.' })}</span>
        <button className={styles.linkBtn} onClick={() => void outstandingQ.refetch()}>{t('outstanding.refresh', { defaultValue: 'Refresh balance' })}</button>
      </div>}
      {resolved && outstandingQ.data?.entries.length === 0 && <div className={styles.cleared} role="status">
        <CheckCircle2 size={20}/><span>{t('outstanding.cleared', { defaultValue: 'Balance cleared. You can charge again.' })}</span>
        <button className={formStyles.btnPrimary} onClick={continueCharging}>
          {t('outstanding.continueCharging', { defaultValue: 'Continue charging' })}
        </button>
      </div>}

      {/* Cards */}
      {defaultPrompt && <section className={styles.defaultPrompt} ref={promptRef} tabIndex={-1}
        aria-labelledby="expired-default-title" aria-describedby="expired-default-body">
        <h2 id="expired-default-title">{t('expiredDefault.title')}</h2>
        <p id="expired-default-body">{t('expiredDefault.body')}</p>
        <div className={styles.defaultActions}>
          <button className={formStyles.btnPrimary} disabled={savingDefault} onClick={() => void acceptDefault()}>
            {savingDefault ? t('form.saving') : t('expiredDefault.makeDefault')}
          </button>
          <button className={formStyles.btnSecondary} disabled={savingDefault} onClick={() => { setDefaultPrompt(null); setDefaultError(''); }}>
            {t('expiredDefault.notNow')}
          </button>
        </div>
        {defaultError && <FormError announce={defaultError.nonce}>{defaultError.message}</FormError>}
      </section>}
      {pmQ.isLoading ? (
        <div className={styles.loading}><Loader2 size={24} className={styles.spin} /></div>
      ) : !pmQ.data?.stripe_configured ? (
        <div className={styles.notice}>
          <AlertTriangle size={18} />
          <span>{t('notConfigured.body')} <code>STRIPE_SECRET_KEY</code>.</span>
        </div>
      ) : (
        <>
          <div className={styles.cardList}>
            {(pmQ.data.payment_methods ?? []).length === 0 ? (
              <div className={styles.empty}>{t('empty')}</div>
            ) : (
              pmQ.data.payment_methods.map((pm) => {
                const isDefault = pmQ.data.default_payment_method === pm.id;
                return (
                  <div key={pm.id} className={`${styles.card} ${isDefault ? styles.cardDefault : ''}`}>
                    <CardBrandIcon brand={pm.brand} />
                    <div className={styles.cardInfo}>
                      <p className={styles.cardLabel}>{BRAND_LABEL[pm.brand ?? ''] ?? pm.brand} •••• {pm.last4}</p>
                      <p className={styles.cardExp}>{t('card.expires', { date: `${String(pm.exp_month).padStart(2, '0')}/${String(pm.exp_year).slice(-2)}` })}</p>
                    </div>
                    {isDefault ? (
                      <span className={styles.defaultTag}><CheckCircle2 size={12} /> {t('card.default')}</span>
                    ) : (
                      <button className={styles.linkBtn} onClick={() => handleSetDefault(pm.id)}>{t('card.setDefault')}</button>
                    )}
                    {hasPayable && <button className={styles.linkBtn}
                      aria-pressed={paymentCard === pm.id} disabled={paying !== null} onClick={() => setSelectedCard(pm.id)}>
                      {paymentCard === pm.id ? t('outstanding.selectedCard', { defaultValue: 'Selected for payment' }) : t('outstanding.useCard', { defaultValue: 'Use this card' })}
                    </button>}
                    <button className={styles.deleteBtn} onClick={() => handleDelete(pm.id, pm.last4)} aria-label={t('card.remove')}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                );
              })
            )}
          </div>

          {!showAdd && (
            <button className={styles.addBtn} onClick={startAdd}>
              <Plus size={18} /> {t('addCard')}
            </button>
          )}
        </>
      )}

      {/* Add card flow */}
      {showAdd && (
        <div className={styles.addCard}>
          {addError && <FormError announce={addError.nonce}>{addError.message}</FormError>}
          {clientSecret && elementsOptions ? (
            <Elements stripe={getStripeP()} options={elementsOptions}>
              <AddCardForm onSuccess={onAdded} onCancel={() => { setShowAdd(false); setClientSecret(null); }} />
            </Elements>
          ) : !addError ? (
            <div className={styles.loading}><Loader2 size={24} className={styles.spin} /></div>
          ) : (
            <button className={formStyles.btnSecondary} onClick={() => setShowAdd(false)}>{t('close')}</button>
          )}
        </div>
      )}

      <p className={styles.applePayNote}>
        {t('applePayNote')}
      </p>
    </div>
  );
}

function AddCardForm({
  onSuccess, onCancel,
}: {
  /** The confirmed inline card. Bank returns are retrieved by the page. */
  onSuccess: (paymentMethodId: string | null) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation('billing');
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useFormError();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    setErr('');

    const result = await stripe.confirmSetup({
      elements,
      confirmParams: { return_url: window.location.href },
      redirect: 'if_required',
    });

    if (result.error) {
      setErr(result.error.message ?? t('errors.saveFailed'));
      setSubmitting(false);
    } else {
      const pmId = confirmedSetupCard(result.setupIntent);
      if (pmId) onSuccess(pmId);
      else { setErr(t('errors.saveFailed')); setSubmitting(false); }
    }
  };

  return (
    <form onSubmit={handleSubmit} className={formStyles.form}>
      {err && <FormError announce={err.nonce}>{err.message}</FormError>}
      <PaymentElement />
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button type="button" className={formStyles.btnSecondary} onClick={onCancel} disabled={submitting}>{t('cancel')}</button>
        <button type="submit" className={formStyles.btnPrimary} disabled={!stripe || submitting}>
          {submitting ? t('form.saving') : t('form.saveCard')}
        </button>
      </div>
    </form>
  );
}

function CardBrandIcon({ brand }: { brand?: string }) {
  return (
    <div className={styles.brandIcon}>
      {brand?.toUpperCase().slice(0, 4) ?? 'CARD'}
    </div>
  );
}
